import posthog from 'posthog-js';
import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { NavLink, useNavigate, useSearchParams } from 'react-router-dom';
import { localDevelopmentBaseUri } from '..';
import { useExchangeAuthorizationCodeMutation } from '../../api/auth';
import logo from '../../assets/bioliberty-logo.svg';
import { Heading } from '../../components/Heading';
import { Spinner } from '../../components/Spinner';
import { MFA_SESSION_STORAGE_KEY, Route } from '../../constants';
import { setToken } from '../../stores/slices/auth';
import { isDev } from '../../util/env';

const authRedirectUri = isDev ? `${localDevelopmentBaseUri}${Route.SignInCallback}` : undefined;

export const SignInCallback = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const exchangedToken = useRef(false);
  const code = searchParams.get('code');
  const state = searchParams.get('state');
  const successfulCallback = code && state;

  const [exchangeAuthorizationCode, { error, isSuccess, data }] = useExchangeAuthorizationCodeMutation();

  useEffect(() => {
    if (exchangedToken.current) {
      return;
    }

    exchangedToken.current = true;

    if (!successfulCallback) {
      posthog.captureException(new Error('Invalid sign in callback search params'), { code, state });
      return;
    }

    exchangeAuthorizationCode({
      authorizationCode: code,
      state: state,
      redirectUri: authRedirectUri
    });
  }, [successfulCallback, code, state, exchangeAuthorizationCode]);

  useEffect(() => {
    if (!isSuccess || !data) return;

    if ('mfaSessionId' in data) {
      // Tokens withheld pending the emailed code; hand off to the OTP view.
      sessionStorage.setItem(MFA_SESSION_STORAGE_KEY, JSON.stringify(data));
      navigate(Route.MfaChallenge, { state: data });
      return;
    }

    if (data.accessToken) {
      dispatch(setToken(data));
    }
    const returnPath = sessionStorage.getItem('returnPath');
    navigate(returnPath ?? Route.Home);
  }, [isSuccess, data, dispatch, navigate]);

  return (
    <section className="flex w-full flex-col items-center justify-center gap-4">
      <img src={logo} alt="logo" className="max-w-48 flex-none sm:max-w-96" />
      {successfulCallback && !error ? <SigningIn /> : <SignInFailed />}
    </section>
  );
};

const SigningIn = () => {
  return (
    <>
      <Heading size="m">Signing in...</Heading>
      <Spinner />
    </>
  );
};

const SignInFailed = () => {
  return (
    <>
      <Heading size="m">Something went wrong</Heading>
      <p>
        We were unable to sign you in. <NavLink to={'/sign-in'}>Try to sign in again</NavLink>
      </p>
    </>
  );
};
